/**
 * ChapterPickerInline — compact "Chapter X of N · Title · ‹ ›" pill that sits
 * above the Listen / Flash / Chunk reader cards. Mirrors the active chapter
 * idx from Reader so switching here also swaps the TTS source text.
 */
import React from 'react';
import { ChevronLeft, ChevronRight, List } from 'lucide-react';
import { Button } from './ui/button';

const ACCENTS = {
  orange: { ring: 'border-orange-500/40', text: 'text-orange-200', tag: 'text-orange-300', hover: 'hover:bg-orange-700/30' },
  purple: { ring: 'border-purple-500/40', text: 'text-purple-200', tag: 'text-purple-300', hover: 'hover:bg-purple-700/30' },
  green: { ring: 'border-emerald-500/40', text: 'text-emerald-200', tag: 'text-emerald-300', hover: 'hover:bg-emerald-700/30' },
};

const ChapterPickerInline = ({ chapters, idx, onChange, accent = 'orange' }) => {
  if (!Array.isArray(chapters) || chapters.length < 2) return null;
  const a = ACCENTS[accent] || ACCENTS.orange;
  const safeIdx = Math.min(Math.max(0, idx || 0), chapters.length - 1);
  const current = chapters[safeIdx];
  const prev = () => onChange(Math.max(0, safeIdx - 1));
  const next = () => onChange(Math.min(chapters.length - 1, safeIdx + 1));

  return (
    <div
      className={`mb-4 flex items-center gap-2 rounded-xl border ${a.ring} bg-slate-900/60 backdrop-blur-sm px-2 py-2`}
      data-testid="reader-mode-chapter-picker"
    >
      <Button
        variant="ghost"
        size="sm"
        disabled={safeIdx === 0}
        onClick={prev}
        className={`${a.text} ${a.hover} h-8 w-8 p-0 disabled:opacity-25`}
        data-testid="reader-mode-chapter-prev"
        aria-label="Previous chapter"
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <select
        value={safeIdx}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className={`flex-1 min-w-0 bg-transparent border-0 text-sm font-semibold text-white focus:outline-none focus:ring-0 cursor-pointer truncate`}
        data-testid="reader-mode-chapter-select"
      >
        {chapters.map((c, i) => (
          <option key={`${c.title}-${i}`} value={i} className="bg-slate-900 text-white">
            Ch. {i + 1} · {c.title || `Chapter ${i + 1}`}
          </option>
        ))}
      </select>
      <span className={`text-[10px] font-mono ${a.tag} hidden sm:inline tabular-nums`}>
        {safeIdx + 1} / {chapters.length}
      </span>
      <List className={`w-3.5 h-3.5 ${a.tag} hidden sm:inline`} />
      <Button
        variant="ghost"
        size="sm"
        disabled={safeIdx === chapters.length - 1}
        onClick={next}
        className={`${a.text} ${a.hover} h-8 w-8 p-0 disabled:opacity-25`}
        data-testid="reader-mode-chapter-next"
        aria-label="Next chapter"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default ChapterPickerInline;
